// Tiny API client + session store for the Flask backend.

const TOKEN_KEY = "cc.token";
const USER_KEY = "cc.user";

function apiBase() {
  const cfg = window.APP_CONFIG || {};
  return (cfg.apiBase || "").replace(/\/+$/, "");
}

function getToken() {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

function setSession(token) {
  // Only the token persists. A full user JSON used to be written under
  // USER_KEY too, but its only reader (getStoredUser) had zero call
  // sites — the app hydrates user state exclusively via Api.me() — so
  // it was write-only PII sitting in localStorage (issue #418).
  // clearSession still removes USER_KEY so blobs persisted by older
  // bundles get cleaned up on the next sign-out/expiry.
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
  } catch (_) { /* storage disabled — session is in-memory only */ }
}

function clearSession() {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(USER_KEY);
  } catch (_) { /* ignore */ }
  // Drop any cached authed-image blob URLs so a subsequent login as a
  // different user (or a re-login after the same user's permissions
  // changed) doesn't reuse images the new session might no longer
  // have access to. Codex P1 on PR #154. Revoke the URLs too — by
  // this point the SPA is unmounting to the AuthScreen so no <img>
  // tag holds a handle.
  _purgeAuthedBlobCache();
}

// Fires when an authenticated request comes back 401 (token expired,
// revoked, or otherwise rejected by the server). Clears stored session
// and dispatches `cc:session-expired` so the App can route to the login
// screen with a banner instead of leaving the user on an opaque
// "Request failed (401)" mid-save. Public-endpoint failures (auth:false
// — login, register, password reset) don't fire this because there's
// no session to expire; they keep their existing inline error UX.
function _emitSessionExpired() {
  clearSession();
  try {
    if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
      window.dispatchEvent(new window.CustomEvent("cc:session-expired"));
    }
  } catch (_) { /* ignore — clearSession() already ran */ }
}

async function apiFetch(path, { method = "GET", body, auth = true } = {}) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = { "Accept": "application/json" };
  // Let the browser set the multipart boundary for FormData uploads.
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  let sentAuth = false;
  if (auth) {
    const t = getToken();
    if (t) {
      headers["Authorization"] = `Bearer ${t}`;
      sentAuth = true;
    }
  }

  let res;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : (isForm ? body : JSON.stringify(body)),
    });
  } catch (err) {
    throw new Error("Couldn't reach the server. Check your connection and try again.");
  }

  let data = null;
  try { data = await res.json(); } catch (_) { /* not all responses are JSON */ }

  if (!res.ok) {
    if (res.status === 401 && sentAuth) _emitSessionExpired();
    // Prefer a human `message` if the backend supplied one; otherwise
    // fall back to the machine-readable `error` code, then a generic
    // string. This lets endpoints return both (e.g. trial_expired) and
    // still display nicely in the UI.
    const msg = (data && (data.message || data.error)) || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.code = data && data.error ? data.error : undefined;
    throw err;
  }
  // Trap (CODE_REVIEW P2 L291): we coerce `null` JSON bodies to `{}`
  // so callers can always destructure the result. If you ever add an
  // endpoint that distinguishes "explicit JSON null" from "empty
  // object", read `res.text()` instead — PR #100/101 broke against
  // this exact assumption when a backend `return jsonify(None)` came
  // back here as `{}` and callers silently saw an empty payload
  // instead of the intended sentinel.
  return data || {};
}

// CODE_REVIEW P2 L268 follow-up — unwrap paginated list responses.
//
// Backend list endpoints now return `{ items: [...], meta: {limit,
// offset, total, has_more} }`. To keep call sites tidy, list methods
// in this file return `{ items, meta }` after running the raw response
// through this helper. Bare-array responses (no envelope) get a synth
// meta of `null` for backward compat with any endpoint we haven't
// enveloped yet (e.g. `/api/events` per the calendar from/to window).
// Throws on a malformed shape so a backend regression surfaces as a
// visible error in the caller's catch instead of as a silent empty list.
function _unwrapList(data) {
  if (Array.isArray(data)) return { items: data, meta: null };
  if (data && Array.isArray(data.items)) {
    return { items: data.items, meta: data.meta || null };
  }
  throw new Error("Couldn't read the list — unexpected response from the server.");
}

// CODE_REVIEW P2 L266 — module-level authed-blob cache with refcount.
//
// Every image thumbnail in marketplace, services, assistance, projects,
// and messages goes through an authed `fetch` → `blob` →
// `URL.createObjectURL` pipeline. Without a cache, an inbox poll every
// 20s re-mounts every thumbnail and re-fetches every blob.
//
// API contract (codex P1 on PR #154 round 3):
//   `authedBlobUrl(path)` returns `Promise<{url, release}>`. Callers
//   call `release()` in their useEffect cleanup so the cache can
//   refcount precisely. URLs are revoked when (a) refcount hits 0 AND
//   the entry has been superseded by a newer fetch / TTL expiry, or
//   (b) clearSession() runs. A long-mounted `<Messages>` thread row
//   keeps its URL valid for as long as it's mounted, regardless of
//   how stale the entry's createdAt looks.
//
// Auth + ACL safety:
//   - TTL'd at 5 min so an in-session ACL change (listing transitions
//     to a closed status while a viewer is still on the page) self-
//     corrects within one TTL window — a fresh acquire after the TTL
//     re-fetches; if the server now returns 403 we surface the fallback.
//   - Purged on `clearSession()` so logout, session expiry, and
//     password rotation all wipe URLs before the next login can reuse
//     them. Aggressive: revoke even if refcount > 0, since on logout
//     the SPA unmounts everything anyway.
//
// FK-cascade orphans (codex P1 round 3) are handled on the backend
// side in `app/models.py`, not here.
const _BLOB_CACHE_TTL_MS = 5 * 60 * 1000;
const _authedBlobCache = new Map();
const _allCreatedBlobUrls = new Set();

function _revokeEntry(entry) {
  if (entry.revoked) return;
  entry.revoked = true;
  Promise.resolve(entry.promise).then(
    (url) => {
      try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
      _allCreatedBlobUrls.delete(url);
    },
    () => { /* ignore — failed fetches have nothing to revoke */ },
  );
}

function _decrementEntry(entry) {
  entry.refCount = Math.max(0, (entry.refCount || 0) - 1);
  if (entry.refCount === 0 && entry.superseded) {
    _revokeEntry(entry);
  }
}

function _maybeRevokeSuperseded(entry) {
  // Called when an entry is marked superseded (TTL refresh or sweep).
  // Revoke immediately if no consumers; otherwise wait for the last
  // release() to drop refCount to 0.
  if (entry.refCount === 0) {
    _revokeEntry(entry);
  }
}

function _purgeAuthedBlobCache() {
  // Aggressive: revoke everything we've ever handed out — including
  // URLs whose entries have already been evicted and entries that
  // still have non-zero refcounts. On logout the SPA tears down to
  // AuthScreen so revoking through mounted <img>s is the desired
  // security behavior.
  for (const url of _allCreatedBlobUrls) {
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  }
  _allCreatedBlobUrls.clear();
  for (const entry of _authedBlobCache.values()) {
    entry.revoked = true; // prevent later _revokeEntry from re-touching
    // In-flight fetches: catch the URL when they resolve, revoke it,
    // AND remove it from the tracking set so the next session start
    // doesn't see a zombie entry that was added after the iteration
    // above had already finished. Codex P3 on PR #154 round 4.
    Promise.resolve(entry.promise).then(
      (url) => {
        try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
        _allCreatedBlobUrls.delete(url);
      },
      () => { /* ignore */ },
    );
  }
  _authedBlobCache.clear();
}

async function authedBlobUrl(path) {
  const now = Date.now();
  let entry = _authedBlobCache.get(path);
  const fresh = entry && now - entry.createdAt < _BLOB_CACHE_TTL_MS;

  if (!fresh) {
    if (entry) {
      // Old entry is being replaced; mark superseded so it can be
      // revoked once its last consumer releases.
      entry.superseded = true;
      _maybeRevokeSuperseded(entry);
    }
    entry = {
      createdAt: now,
      refCount: 0,
      superseded: false,
      revoked: false,
      promise: null,
    };
    const newEntry = entry;
    newEntry.promise = (async () => {
      try {
        const t = getToken();
        const res = await fetch(`${apiBase()}${path}`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {},
        });
        if (!res.ok) throw new Error(`image fetch failed (${res.status})`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        _allCreatedBlobUrls.add(url);
        return url;
      } catch (err) {
        // Don't leave a failed entry in the cache; next acquire retries.
        if (_authedBlobCache.get(path) === newEntry) {
          _authedBlobCache.delete(path);
        }
        throw err;
      }
    })();
    _authedBlobCache.set(path, entry);
  }

  // Reserve a refcount slot BEFORE awaiting so a mid-flight TTL refresh
  // or clearSession can't free the URL out from under us.
  entry.refCount++;
  const localEntry = entry;
  let released = false;
  const release = () => {
    // Idempotent — React strict mode double-invokes useEffect cleanup
    // in dev and a defensive caller may also call release twice.
    if (released) return;
    released = true;
    _decrementEntry(localEntry);
  };

  try {
    const url = await entry.promise;
    return { url, release };
  } catch (err) {
    // Fetch failed; give the refcount slot back so a stale entry
    // can't zombie on a non-zero refcount.
    release();
    throw err;
  }
}

// Periodic sweep: evict entries older than TTL from the map and mark
// them superseded. Mounted consumers (refCount > 0) keep their URLs
// alive until release; the URL is revoked at the moment the last
// consumer drops, not on age alone. This is what makes a long-mounted
// `<Messages>` row safe — codex P1 on PR #154 round 3.
function _sweepStaleAuthedBlobs() {
  const cutoff = Date.now() - _BLOB_CACHE_TTL_MS;
  for (const [path, entry] of _authedBlobCache.entries()) {
    if (entry.createdAt < cutoff) {
      _authedBlobCache.delete(path);
      entry.superseded = true;
      _maybeRevokeSuperseded(entry);
    }
  }
}

if (typeof window !== "undefined" && typeof setInterval === "function") {
  setInterval(_sweepStaleAuthedBlobs, _BLOB_CACHE_TTL_MS);
}

// Backend role -> the 3-bucket role the frontend router understands.
function uiRole(role) {
  if (role === "super_admin") return "admin";
  if (role === "company_admin") return "manager";
  if (role === "manager") return "manager";
  return "homeowner";
}

const Api = {
  base: apiBase,
  getToken,
  setSession,
  clearSession,
  uiRole,
  authedBlobUrl,

  async switchCommunity({ community_id }) {
    return apiFetch("/api/auth/switch-community", {
      method: "POST",
      body: { community_id },
    });
  },

  async passwordResetRequest({ email }) {
    return apiFetch("/api/auth/password-reset/request", {
      method: "POST",
      auth: false,
      body: { email },
    });
  },

  async passwordResetConfirm({ token, password }) {
    return apiFetch("/api/auth/password-reset/confirm", {
      method: "POST",
      auth: false,
      body: { token, password },
    });
  },

  async login({ email, password, community_slug, community_id }) {
    const data = await apiFetch("/api/auth/login", {
      method: "POST",
      auth: false,
      body: { email, password, community_slug, community_id },
    });
    // Multi-community response carries no token; caller will re-call
    // with a chosen community_id.
    if (data && data.token) setSession(data.token);
    return data;
  },

  async registerHomeowner({ email, password, full_name, community_slug }) {
    const data = await apiFetch("/api/auth/register-homeowner", {
      method: "POST",
      auth: false,
      body: { email, password, full_name, community_slug },
    });
    // Signup may return `email_verification_required: true` instead
    // of a token (CODE_REVIEW P2 L228). Only seed the session when
    // we actually got a JWT — caller's auth UI handles the
    // verification-required branch separately.
    if (data && data.token) setSession(data.token);
    return data;
  },

  async registerCommunity({ email, password, full_name, community_name, state, unit_count, company_setup, company_name, cf_turnstile_token }) {
    const data = await apiFetch("/api/auth/register-community", {
      method: "POST",
      auth: false,
      body: { email, password, full_name, community_name, state, unit_count, company_setup, company_name, cf_turnstile_token },
    });
    if (data && data.token) setSession(data.token);
    return data;
  },

  // Public runtime config — currently just the Turnstile site key
  // (issue #175). Cached on APP_CONFIG so a re-call inside the same
  // session doesn't hammer the endpoint, but ONLY on a SUCCESSFUL
  // fetch. Transient failures (network blip, 429) leave the cache
  // as null so a subsequent call retries — codex P2 on PR #192
  // caught the prior shape, which permanently cached "" on failure
  // and left users stranded with no widget while the server still
  // enforced the CAPTCHA. Returning "" for the failing call is
  // fine; the next call (e.g., AuthScreen's retry after a delay)
  // does a fresh fetch.
  async publicConfig() {
    if (window.APP_CONFIG && window.APP_CONFIG.turnstileSiteKey !== null
        && window.APP_CONFIG.turnstileSiteKey !== undefined) {
      return { turnstile_site_key: window.APP_CONFIG.turnstileSiteKey };
    }
    try {
      const data = await apiFetch("/api/auth/public-config", { auth: false });
      const key = (data && data.turnstile_site_key) || "";
      // Only cache the SUCCESSFUL result — both an explicit "" from
      // the backend ("Turnstile is unconfigured here") and a real
      // site key are valid outcomes worth caching.
      if (window.APP_CONFIG) window.APP_CONFIG.turnstileSiteKey = key;
      return data;
    } catch (_) {
      // Don't cache failure. Return "" so the immediate caller can
      // fall back to "no CAPTCHA" rendering, but leave the cache
      // null so the next call retries. The caller is responsible
      // for retrying if it cares about getting a real key.
      return { turnstile_site_key: "" };
    }
  },

  async verifyEmail({ token }) {
    return apiFetch("/api/auth/verify-email", {
      method: "POST",
      auth: false,
      body: { token },
    });
  },

  async verifyEmailResend({ email }) {
    return apiFetch("/api/auth/verify-email/resend", {
      method: "POST",
      auth: false,
      body: { email },
    });
  },

  // Public, used by the homeowner signup form's community picker.
  async communitiesLookup({ state } = {}) {
    const q = state ? `?state=${encodeURIComponent(state)}` : "";
    return apiFetch(`/api/auth/communities-lookup${q}`, { auth: false });
  },

  async me() {
    return apiFetch("/api/auth/me");
  },

  async updateProfile({ full_name, email }) {
    const body = {};
    if (full_name !== undefined) body.full_name = full_name;
    if (email !== undefined) body.email = email;
    const data = await apiFetch("/api/auth/me", { method: "PATCH", body });
    // Refresh the cached user blob so reloads pick up the changes.
    try {
      const tok = getToken();
      if (tok) setSession(tok);
    } catch (_) { /* ignore storage failures */ }
    return data;
  },

  async changePassword({ current_password, new_password }) {
    return apiFetch("/api/auth/change-password", {
      method: "POST",
      body: { current_password, new_password },
    });
  },

  async closeAccount({ password }) {
    const data = await apiFetch("/api/auth/me", {
      method: "DELETE",
      body: { password },
    });
    clearSession();
    return data;
  },

  async getPreferences() {
    return apiFetch("/api/auth/me/preferences");
  },
  async updatePreferences(patch) {
    return apiFetch("/api/auth/me/preferences", { method: "PATCH", body: patch });
  },

  // ─── OAuth (PR A login + PR B1 link/unlink/list) ─────────────────────────
  // The OAuth dance runs in the browser:
  //   1. UI calls oauthStart(provider) — unauthenticated. Backend
  //      mints a state token, returns { auth_url, state }. UI sends
  //      the browser to auth_url.
  //   2. Provider redirects to /api/auth/oauth/<provider>/callback,
  //      which 302s to OAUTH_CALLBACK_REDIRECT (the SPA's
  //      /oauth-callback page) with a URL fragment:
  //         #temp=<jwt>           → login flow
  //         #linked=<provider>    → link flow succeeded
  //         #error=<reason>       → either flow failed
  //   3. The OAuthCallback page reads the fragment and dispatches:
  //         login  → oauthFinalize({ temp_token, community_id? })
  //         link   → toast + return to settings
  //         error  → show reason
  //
  // For link mode from an authenticated user (account settings):
  //   1. UI calls oauthLinkStart(provider) — auth required. Backend
  //      mints a state token carrying mode=link + the user's id.
  //   2. Same dance as above; callback redirects with #linked=<provider>.
  async oauthStart(provider, platform) {
    // `platform` is optional: "ios" tells the backend to bake a
    // platform=ios claim into the signed state token, so the eventual
    // callback redirect goes to the iOS app's custom URL scheme
    // instead of the web SPA. Anything else (undefined / "web") keeps
    // the existing HTTPS redirect.
    const qs = platform ? `?platform=${encodeURIComponent(platform)}` : "";
    return apiFetch(
      `/api/auth/oauth/${encodeURIComponent(provider)}/start${qs}`,
      { auth: false },
    );
  },
  async oauthFinalize({ temp_token, state_nonce, community_id, community_slug } = {}) {
    const data = await apiFetch("/api/auth/oauth/finalize", {
      method: "POST",
      auth: false,
      body: { temp_token, state_nonce, community_id, community_slug },
    });
    if (data && data.token) setSession(data.token);
    return data;
  },
  async oauthListIdentities() {
    return apiFetch("/api/auth/oauth/identities");
  },
  async oauthLinkStart(provider) {
    return apiFetch(
      `/api/auth/oauth/${encodeURIComponent(provider)}/link/start`,
      { method: "POST" },
    );
  },
  async oauthUnlink(provider) {
    return apiFetch(
      `/api/auth/oauth/${encodeURIComponent(provider)}`,
      { method: "DELETE" },
    );
  },

  // ─── Push-notification device tokens (iOS) ───────────────────────────────
  async registerDevice({ token, platform = "ios" }) {
    return apiFetch("/api/notifications/devices", {
      method: "POST",
      body: { token, platform },
    });
  },
  async unregisterDevice({ token }) {
    return apiFetch("/api/notifications/devices", {
      method: "DELETE",
      body: { token },
    });
  },

  // ─── PMC portfolio (COMPANY_ADMIN-scoped) ────────────────────────────────
  // A COMPANY_ADMIN whose active community has company_id set can fetch
  // their PMC's portfolio + initiate checkout from these endpoints.
  // See pricing.md v10 for the PMC tier structure.
  async pmcPortfolio() {
    return apiFetch("/api/pmc/portfolio");
  },
  async pmcCheckout({ success_url, cancel_url }) {
    return apiFetch("/api/pmc/checkout", {
      method: "POST",
      body: { success_url, cancel_url },
    });
  },
  // ─── Household ───────────────────────────────────────────────────────────
  // A household = one billable unit inside an HOA. Each household has
  // a primary (AI-enabled seat) plus up to 5 family-member secondaries
  // (community boards only). See pricing.md v10.
  async getHousehold() {
    return apiFetch("/api/household");
  },
  async updateHousehold(patch) {
    return apiFetch("/api/household", { method: "PATCH", body: patch });
  },
  async createHouseholdInvite({ email, full_name }) {
    return apiFetch("/api/household/invites", {
      method: "POST",
      body: { email, full_name },
    });
  },
  async deleteHouseholdInvite(invite_id) {
    return apiFetch(`/api/household/invites/${encodeURIComponent(invite_id)}`, {
      method: "DELETE",
    });
  },
  async transferHouseholdPrimary(to_membership_id) {
    return apiFetch("/api/household/transfer-primary", {
      method: "POST",
      body: { to_membership_id },
    });
  },
  async removeHouseholdMember(membership_id) {
    return apiFetch(`/api/household/members/${encodeURIComponent(membership_id)}`, {
      method: "DELETE",
    });
  },
  async updateMemberPermissions(membership_id, permissions) {
    return apiFetch(`/api/household/members/${encodeURIComponent(membership_id)}/permissions`, {
      method: "PATCH",
      body: permissions,
    });
  },
  // Public — no auth header. Token comes from the email link.
  async acceptHouseholdInvite({ token, password, full_name, existing_account_password }) {
    const data = await apiFetch("/api/household/accept-invite", {
      method: "POST",
      auth: false,
      body: { token, password, full_name, existing_account_password },
    });
    if (data && data.token) setSession(data.token);
    return data;
  },

  // ─── Billing (Stripe) ────────────────────────────────────────────────────
  async billingStatus() {
    return apiFetch("/api/billing/status");
  },
  async billingCheckout({ price_id, success_url, cancel_url }) {
    return apiFetch("/api/billing/checkout", {
      method: "POST",
      body: { price_id, success_url, cancel_url },
    });
  },
  async billingPortal({ return_url }) {
    return apiFetch("/api/billing/portal", {
      method: "POST",
      body: { return_url },
    });
  },
  // Self-serve +7 day trial extension (max 2 per community). Manager-only.
  async billingExtendTrial() {
    return apiFetch("/api/billing/extend-trial", { method: "POST" });
  },

  // ─── Community (any-member-scoped) ───────────────────────────────────────
  async communityDirectory() {
    return apiFetch("/api/community/directory");
  },

  // ─── Events ──────────────────────────────────────────────────────────────
  async eventsList({ from, to } = {}) {
    const params = [];
    if (from) params.push(`from=${encodeURIComponent(from)}`);
    if (to) params.push(`to=${encodeURIComponent(to)}`);
    const q = params.length ? `?${params.join("&")}` : "";
    return apiFetch(`/api/events${q}`);
  },
  async eventCreate(event) {
    return apiFetch("/api/events", { method: "POST", body: event });
  },
  async eventUpdate(id, patch) {
    return apiFetch(`/api/events/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
  },
  async eventDelete(id) {
    return apiFetch(`/api/events/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Calendar event proposals — PR #1 of today's batch. eventCreate
  // already routes through here for both manager (auto-approve) and
  // homeowner (creates pending) cases; the server decides which by
  // role. These two are the manager-side review surface.
  async eventsPending() {
    return apiFetch("/api/events/pending");
  },
  async eventModerate(id, action) {
    return apiFetch(`/api/events/${encodeURIComponent(id)}/moderate`, {
      method: "POST", body: { action },
    });
  },

  // ─── Community admin (manager-scoped) ────────────────────────────────────
  async adminCommunity() {
    return apiFetch("/api/admin/community");
  },
  async adminCommunityUsers() {
    return apiFetch("/api/admin/community/users");
  },
  async adminCommunityTopBylawCitations() {
    return apiFetch("/api/admin/community/top-bylaw-citations");
  },
  async adminCommunityUpdate(patch) {
    return apiFetch("/api/admin/community", { method: "PATCH", body: patch });
  },
  // PR 6: pending decrease-request lifecycle. Manager scope.
  async adminCommunityPendingUnitCountChange() {
    return apiFetch("/api/admin/community/pending-unit-count-change");
  },
  async adminCommunityCancelPendingUnitCountChange() {
    return apiFetch("/api/admin/community/pending-unit-count-change/cancel", {
      method: "POST",
    });
  },
  // Super-admin operator queue.
  async adminListUnitCountChanges(status) {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiFetch(`/api/admin/unit-count-changes${q}`);
  },
  async adminApproveUnitCountChange(request_id, note) {
    return apiFetch(
      `/api/admin/unit-count-changes/${encodeURIComponent(request_id)}/approve`,
      { method: "POST", body: { note } },
    );
  },
  async adminDenyUnitCountChange(request_id, note) {
    return apiFetch(
      `/api/admin/unit-count-changes/${encodeURIComponent(request_id)}/deny`,
      { method: "POST", body: { note } },
    );
  },
  async adminSetUserRole(user_id, role) {
    return apiFetch(`/api/admin/community/users/${encodeURIComponent(user_id)}/role`, {
      method: "POST",
      body: { role },
    });
  },
  // Manager pauses (or restores) a member's access to Consult
  // Compass + Project Builder polish in this community. Per-
  // membership; doesn't touch the user's global account or their
  // AI access in any other community they belong to.
  async managerSetMemberAiSuspended(user_id, suspended) {
    return apiFetch(`/api/admin/community/users/${encodeURIComponent(user_id)}/ai-suspended`, {
      method: "PATCH",
      body: { suspended: !!suspended },
    });
  },
  // Operator changes a user's role in an arbitrary community.
  async adminSetCommunityUserRole(community_id, user_id, role) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/users/${encodeURIComponent(user_id)}/role`,
      { method: "POST", body: { role } },
    );
  },

  // Super-admin: drill into / soft-delete arbitrary community + user.
  async adminGetCommunity(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}`);
  },
  async adminListCommunityUsers(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}/users`);
  },
  async adminDeactivateCommunity(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}`, {
      method: "DELETE",
    });
  },
  async adminReactivateCommunity(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}/reactivate`, {
      method: "POST",
    });
  },
  async adminPermanentlyDeleteCommunity(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}?permanent=true`, {
      method: "DELETE",
    });
  },
  async adminDeactivateUser(community_id, user_id) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/users/${encodeURIComponent(user_id)}`,
      { method: "DELETE" },
    );
  },
  async adminReactivateUser(community_id, user_id) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/users/${encodeURIComponent(user_id)}/reactivate`,
      { method: "POST" },
    );
  },
  // Operator: read + forgive polish usage on any community. The
  // backend hard-deletes the most-recent N polish events (or all
  // of them on reset), so the manager-side meter reflects the new
  // count immediately.
  async adminGetPolishUsage(community_id) {
    return apiFetch(`/api/admin/communities/${encodeURIComponent(community_id)}/polish-usage`);
  },
  async adminAdjustPolishUsage(community_id, body) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/polish-adjust`,
      { method: "POST", body },
    );
  },
  // Operator: every user in the system (for the Users panel).
  // Returns the standard `{items, meta}` shape via `_unwrapList` so
  // the SPA-side pager (issue #159) and the existing
  // throw-on-malformed-shape behaviour (codex P2 on PR #145) both
  // ride the shared helper. `q` and `orphans_only` are server-side
  // filters — they span every page, not just the currently-rendered
  // slice, which is the whole point of the orphan-finding fix.
  async adminListAllUsers({ limit, offset, q, orphans_only } = {}) {
    const p = new URLSearchParams();
    if (limit != null) p.set("limit", limit);
    if (offset != null) p.set("offset", offset);
    if (q) p.set("q", q);
    if (orphans_only) p.set("orphans_only", "1");
    const qs = p.toString();
    const data = await apiFetch(`/api/admin/users${qs ? "?" + qs : ""}`);
    return _unwrapList(data);
  },
  async adminDeleteUser(user_id) {
    return apiFetch(`/api/admin/users/${encodeURIComponent(user_id)}`, {
      method: "DELETE",
    });
  },
  // Manager: drop a user from this community without deactivating
  // their global account.
  // ─── Notifications ──────────────────────────────────────────────────────
  async notificationsList({ unread_only = false } = {}) {
    const q = unread_only ? "?unread_only=1" : "";
    return apiFetch(`/api/notifications${q}`);
  },
  async notificationsUnreadCount() {
    return apiFetch("/api/notifications/unread-count");
  },
  async notificationsMarkRead(id) {
    return apiFetch(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
  },
  async notificationsMarkAllRead() {
    return apiFetch("/api/notifications/read-all", { method: "POST" });
  },

  // ─── Messages ───────────────────────────────────────────────────────────
  async messagesUnreadCount() {
    return apiFetch("/api/messages/unread-count");
  },
  async messagesThreads({ limit, offset } = {}) {
    const p = new URLSearchParams();
    if (limit != null) p.set("limit", limit);
    if (offset != null) p.set("offset", offset);
    const qs = p.toString();
    const data = await apiFetch(`/api/messages/threads${qs ? "?" + qs : ""}`);
    return _unwrapList(data);
  },
  async messagesThread(thread_id) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}`);
  },
  async messagesCreateThread({ recipient_user_id, body, related_listing_id, related_service_id, related_assistance_id, subject }) {
    return apiFetch("/api/messages/threads", {
      method: "POST",
      body: { recipient_user_id, body, related_listing_id, related_service_id, related_assistance_id, subject },
    });
  },
  async messagesSend(thread_id, body) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}/messages`, {
      method: "POST", body: { body },
    });
  },
  async messagesMarkRead(thread_id) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}/read`, {
      method: "POST",
    });
  },
  async messagesHideThread(thread_id) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}`, {
      method: "DELETE",
    });
  },
  async messagesAcceptThread(thread_id) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}/accept`, {
      method: "POST",
    });
  },
  async messagesDenyThread(thread_id) {
    return apiFetch(`/api/messages/threads/${encodeURIComponent(thread_id)}/deny`, {
      method: "POST",
    });
  },

  // ─── Marketplace ─────────────────────────────────────────────────────────
  async marketplaceUsage() {
    return apiFetch("/api/marketplace/usage");
  },
  async marketplaceList({ category, status, mine_only, q, limit, offset } = {}) {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    if (mine_only) p.set("mine_only", "1");
    if (q) p.set("q", q);
    if (limit != null) p.set("limit", limit);
    if (offset != null) p.set("offset", offset);
    const qs = p.toString();
    const data = await apiFetch(`/api/marketplace/listings${qs ? "?" + qs : ""}`);
    return _unwrapList(data);
  },
  async marketplaceGet(id) {
    return apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`);
  },
  async marketplaceCreate(body) {
    return apiFetch("/api/marketplace/listings", { method: "POST", body });
  },
  async marketplaceUpdate(id, body) {
    return apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`, {
      method: "PATCH", body,
    });
  },
  async marketplaceDelete(id) {
    return apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async marketplaceUploadImage(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}/images`, {
      method: "POST", body: fd,
    });
  },
  async marketplaceDeleteImage(image_id) {
    return apiFetch(`/api/marketplace/images/${encodeURIComponent(image_id)}`, { method: "DELETE" });
  },
  // (Removed `marketplaceImageUrl` — it returned an unauthenticated URL
  // string that no caller used. Images are served via `AuthedImage` /
  // `authedBlobUrl` which sends the bearer header. Keeping a public
  // URL-builder around invited misuse.)

  // ─── Services (neighbors offering services) ────────────────────────────
  async servicesUsage() { return apiFetch("/api/services/usage"); },
  async servicesList({ category, status, mine_only, q, limit, offset } = {}) {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    if (mine_only) p.set("mine_only", "1");
    if (q) p.set("q", q);
    if (limit != null) p.set("limit", limit);
    if (offset != null) p.set("offset", offset);
    const qs = p.toString();
    const data = await apiFetch(`/api/services/offers${qs ? "?" + qs : ""}`);
    return _unwrapList(data);
  },
  async servicesGet(id) {
    return apiFetch(`/api/services/offers/${encodeURIComponent(id)}`);
  },
  async servicesCreate(body) {
    return apiFetch("/api/services/offers", { method: "POST", body });
  },
  async servicesUpdate(id, body) {
    return apiFetch(`/api/services/offers/${encodeURIComponent(id)}`, { method: "PATCH", body });
  },
  async servicesDelete(id) {
    return apiFetch(`/api/services/offers/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async servicesUploadImage(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch(`/api/services/offers/${encodeURIComponent(id)}/images`, { method: "POST", body: fd });
  },
  async servicesDeleteImage(image_id) {
    return apiFetch(`/api/services/images/${encodeURIComponent(image_id)}`, { method: "DELETE" });
  },

  // Service reviews — neighbor reputation. Tied to a specific offer
  // but the provider's aggregate (avg + count) also rolls up across
  // every offer they have in the community.
  async servicesOfferReviews(offer_id) {
    return apiFetch(`/api/services/offers/${encodeURIComponent(offer_id)}/reviews`);
  },
  async servicesReviewCreate(offer_id, { rating, body }) {
    return apiFetch(`/api/services/offers/${encodeURIComponent(offer_id)}/reviews`, {
      method: "POST", body: { rating, body },
    });
  },
  async servicesReviewUpdate(review_id, { rating, body }) {
    return apiFetch(`/api/services/reviews/${encodeURIComponent(review_id)}`, {
      method: "PATCH", body: { rating, body },
    });
  },
  async servicesReviewDelete(review_id) {
    return apiFetch(`/api/services/reviews/${encodeURIComponent(review_id)}`, { method: "DELETE" });
  },

  // ─── Assistance (neighbors asking for help) ─────────────────────────────
  async assistanceUsage() { return apiFetch("/api/assistance/usage"); },
  async assistanceList({ category, urgency, status, mine_only, q, limit, offset } = {}) {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (urgency) p.set("urgency", urgency);
    if (status) p.set("status", status);
    if (mine_only) p.set("mine_only", "1");
    if (q) p.set("q", q);
    if (limit != null) p.set("limit", limit);
    if (offset != null) p.set("offset", offset);
    const qs = p.toString();
    const data = await apiFetch(`/api/assistance/requests${qs ? "?" + qs : ""}`);
    return _unwrapList(data);
  },
  async assistanceGet(id) {
    return apiFetch(`/api/assistance/requests/${encodeURIComponent(id)}`);
  },
  async assistanceCreate(body) {
    return apiFetch("/api/assistance/requests", { method: "POST", body });
  },
  async assistanceUpdate(id, body) {
    return apiFetch(`/api/assistance/requests/${encodeURIComponent(id)}`, { method: "PATCH", body });
  },
  async assistanceDelete(id) {
    return apiFetch(`/api/assistance/requests/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async assistanceUploadImage(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch(`/api/assistance/requests/${encodeURIComponent(id)}/images`, { method: "POST", body: fd });
  },
  async assistanceDeleteImage(image_id) {
    return apiFetch(`/api/assistance/images/${encodeURIComponent(image_id)}`, { method: "DELETE" });
  },

  // ─── Project Builder (ARC requests) ────────────────────────────────────
  async projectsList({ mine, board, status } = {}) {
    const p = new URLSearchParams();
    if (mine) p.set("mine", "1");
    if (board) p.set("board", "1");
    if (status) p.set("status", status);
    const qs = p.toString();
    return apiFetch(`/api/projects${qs ? "?" + qs : ""}`);
  },
  async projectsGet(id) {
    return apiFetch(`/api/projects/${encodeURIComponent(id)}`);
  },
  async projectsCreate(body) {
    return apiFetch("/api/projects", { method: "POST", body });
  },
  async projectsUpdate(id, body) {
    return apiFetch(`/api/projects/${encodeURIComponent(id)}`, { method: "PATCH", body });
  },
  async projectsDelete(id) {
    return apiFetch(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async projectsPolish(id) {
    return apiFetch(`/api/projects/${encodeURIComponent(id)}/polish`, { method: "POST" });
  },
  /**
   * Save (or clear) the resident's response to one bylaw finding.
   *
   * `finding_id` is the stable 12-hex id (issue #163) emitted by the
   * polish service on each finding — `finding.id`. We also send the
   * `section` text in the body so the saved entry is self-describing
   * and re-polish can replay the resident's response keyed to a
   * specific section, even if Claude reorders findings on the next
   * polish.
   *
   * `answers` is a list of {label, value} pairs derived from the
   * compliance_actions Claude generated for this finding. Labels
   * mirror Claude's labels (rule-specific, not a global allowlist).
   * Boolean value=true means a "check" action was ticked; string
   * values come from "text" actions.
   *
   * Pass response="" and answers=[] to delete the saved entry.
   */
  async projectsUpsertFindingResponse(id, finding_id, { section, response, answers }) {
    return apiFetch(
      `/api/projects/${encodeURIComponent(id)}/finding-responses/${encodeURIComponent(finding_id)}`,
      {
        method: "PUT",
        body: {
          section: section || "",
          response: response || "",
          answers: answers || [],
        },
      },
    );
  },
  async projectsPolishUsage() {
    return apiFetch("/api/projects/polish-usage");
  },

  // Combined hourly quota status for the My Quota page. Returns one
  // meter per AI feature (Consult Compass, Project Builder polish).
  async quota() {
    return apiFetch("/api/quota");
  },
  async projectsUploadImage(id, file, kind = "current") {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    return apiFetch(`/api/projects/${encodeURIComponent(id)}/images`, { method: "POST", body: fd });
  },
  async projectsDeleteImage(image_id) {
    return apiFetch(`/api/projects/images/${encodeURIComponent(image_id)}`, { method: "DELETE" });
  },
  /**
   * Download a project as a Microsoft Word .docx. Returns a Blob —
   * the caller is responsible for wiring it to an <a download>
   * trigger. Throws on non-2xx so a failed download surfaces in the
   * UI instead of saving a 401/404 body to disk.
   */
  async projectsDownload(id) {
    const token = this.getToken();
    const apiBase = window.APP_CONFIG?.apiBase || "";
    const resp = await fetch(
      `${apiBase}/api/projects/${encodeURIComponent(id)}/download`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!resp.ok) {
      // Same session-expiry path apiFetch takes — see _emitSessionExpired.
      if (resp.status === 401 && token) _emitSessionExpired();
      let msg = `Download failed (${resp.status})`;
      try {
        const j = await resp.json();
        if (j?.error || j?.message) msg = j.message || j.error;
      } catch (_) { /* non-JSON error body */ }
      throw new Error(msg);
    }
    const disposition = resp.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^"]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : `project_${id}.docx`;
    const blob = await resp.blob();
    return { blob, filename };
  },

  async managerRemoveMembership(user_id) {
    return apiFetch(
      `/api/admin/community/users/${encodeURIComponent(user_id)}/membership`,
      { method: "DELETE" },
    );
  },

  async adminAddCommunityMember(community_id, { email, role }) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/members`,
      { method: "POST", body: { email, role } },
    );
  },
  async adminRemoveCommunityMember(community_id, user_id) {
    return apiFetch(
      `/api/admin/communities/${encodeURIComponent(community_id)}/members/${encodeURIComponent(user_id)}`,
      { method: "DELETE" },
    );
  },

  // ─── Bylaws ──────────────────────────────────────────────────────────────
  async bylawsList({ include_inactive = false } = {}) {
    const q = include_inactive ? "?include_inactive=true" : "";
    return apiFetch(`/api/bylaws${q}`);
  },
  async bylawsDeactivate(doc_id) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}`, { method: "DELETE" });
  },
  async bylawsReactivate(doc_id) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}/reactivate`, { method: "POST" });
  },
  async bylawsRename(doc_id, title) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}`, {
      method: "PATCH",
      body: { title },
    });
  },
  async bylawsPermanentlyDelete(doc_id) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}?permanent=true`, { method: "DELETE" });
  },
  async bylawsUpload({ file, title, description }) {
    const form = new FormData();
    form.append("file", file);
    if (title) form.append("title", title);
    if (description) form.append("description", description);
    return apiFetch("/api/bylaws/upload", { method: "POST", body: form });
  },
  async bylawsHistory(doc_id) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}/history`);
  },
  async bylawsContent(doc_id) {
    return apiFetch(`/api/bylaws/${encodeURIComponent(doc_id)}/content`);
  },

  // ─── Chat ────────────────────────────────────────────────────────────────
  async chatAsk({ question, session_id }) {
    const body = { question };
    if (session_id) body.session_id = session_id;
    return apiFetch("/api/chat/ask", { method: "POST", body });
  },
  async chatSessions() {
    return apiFetch("/api/chat/sessions");
  },
  async chatSession(public_id) {
    return apiFetch(`/api/chat/sessions/${encodeURIComponent(public_id)}`);
  },

  // ─── Super-admin dashboard ───────────────────────────────────────────────
  async dashboardOverview() {
    return apiFetch("/api/dashboard/overview");
  },
  async dashboardCommunities() {
    return apiFetch("/api/dashboard/communities");
  },
  async dashboardRevenue() {
    return apiFetch("/api/dashboard/revenue");
  },
  async dashboardTrends({ days = 30 } = {}) {
    return apiFetch(`/api/dashboard/trends?days=${encodeURIComponent(days)}`);
  },
  async adminHealthSnapshot() {
    return apiFetch("/api/admin/health/snapshot");
  },
  async adminHealthJobs() {
    return apiFetch("/api/admin/health/jobs");
  },
  async adminHealthWebhooks() {
    return apiFetch("/api/admin/health/webhooks");
  },
  async adminHealthAiUsage() {
    return apiFetch("/api/admin/health/ai-usage");
  },

  // ── Town Square ────────────────────────────────────────────────────
  async townSquareCategories() {
    return apiFetch("/api/town-square/categories");
  },
  async townSquareCreateCategory({ name }) {
    return apiFetch("/api/town-square/categories", {
      method: "POST",
      body: { name },
    });
  },
  async townSquareSetCategoryActive(categoryId, is_active) {
    return apiFetch(`/api/town-square/categories/${encodeURIComponent(categoryId)}`, {
      method: "PATCH",
      body: { is_active },
    });
  },
  async townSquareDeleteCategory(categoryId) {
    return apiFetch(`/api/town-square/categories/${encodeURIComponent(categoryId)}`, {
      method: "DELETE",
    });
  },
  async townSquareTopics({ category, page = 1, filter, q } = {}) {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (page) params.set("page", String(page));
    if (filter) params.set("filter", filter);
    if (q) params.set("q", q);
    const qs = params.toString();
    return apiFetch(`/api/town-square/topics${qs ? "?" + qs : ""}`);
  },
  async townSquareCreateTopic({ title, body, category }) {
    return apiFetch("/api/town-square/topics", {
      method: "POST",
      body: { title, body, category },
    });
  },
  async townSquareGetTopic(topicId) {
    return apiFetch(`/api/town-square/topics/${encodeURIComponent(topicId)}`);
  },
  async townSquareReply(topicId, body) {
    return apiFetch(`/api/town-square/topics/${encodeURIComponent(topicId)}/replies`, {
      method: "POST",
      body: { body },
    });
  },
  async townSquareReactTopic(topicId) {
    return apiFetch(`/api/town-square/topics/${encodeURIComponent(topicId)}/react`, {
      method: "POST",
    });
  },
  async townSquareReactReply(replyId) {
    return apiFetch(`/api/town-square/replies/${encodeURIComponent(replyId)}/react`, {
      method: "POST",
    });
  },
  async townSquareModerate(topicId, patch) {
    return apiFetch(`/api/town-square/topics/${encodeURIComponent(topicId)}/moderate`, {
      method: "POST",
      body: patch,
    });
  },

  // ── Sub-Communities (interest groups inside an HOA) ─────────────────
  // Issue #216. Slice 2 surfaces list/create/join/leave; the
  // member/request/role/remove helpers below are wired now so slice 5
  // (moderation UI) doesn't have to touch api.jsx again.
  async subCommunities() {
    return apiFetch("/api/sub-communities/");
  },
  async subCommunityCreate({ name, description }) {
    return apiFetch("/api/sub-communities/", {
      method: "POST",
      body: { name, description },
    });
  },
  async subCommunity(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}`);
  },
  async subCommunityJoin(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/join`, {
      method: "POST",
    });
  },
  async subCommunityLeave(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/leave`, {
      method: "POST",
    });
  },
  async subCommunityMembers(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/members`);
  },
  async subCommunityRequests(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/requests`);
  },
  async subCommunityResolveRequest(publicId, requestId, action) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/requests/${requestId}`,
      { method: "POST", body: { action } },
    );
  },
  async subCommunityUpdateRole(publicId, userId, role) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/members/${userId}`,
      { method: "PATCH", body: { role } },
    );
  },
  async subCommunityRemoveMember(publicId, userId) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/members/${userId}`,
      { method: "DELETE" },
    );
  },

  // Sub-community bulletin posts (slice 3 of #216). Cursor-paginated
  // (issue #417): pass the last rendered post's id as `before` for
  // the next-older page; response carries has_more. Cursor over
  // offset so a post created between fetches can't duplicate/skip
  // items at the page boundary (codex P2 on PR #427).
  async subCommunityPosts(publicId, before = null) {
    // Cursor walks the unpinned timeline only — pinned posts arrive
    // in full on the first page (PR #427). `before` is the id of the
    // last UNPINNED post rendered; its (created_at, id) coordinates
    // are immutable, so pin flips can't reshuffle pages.
    const qs = before ? `?before=${encodeURIComponent(before)}` : "";
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/posts${qs}`
    );
  },
  async subCommunityCreatePost(publicId, { title, body }) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/posts`, {
      method: "POST",
      body: { title, body },
    });
  },
  async subCommunityUpdatePost(publicId, postId, patch) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/posts/${encodeURIComponent(postId)}`,
      { method: "PATCH", body: patch },
    );
  },
  async subCommunityDeletePost(publicId, postId) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/posts/${encodeURIComponent(postId)}`,
      { method: "DELETE" },
    );
  },

  // Sub-community group chat (slice 4 of #216). Cursor pagination —
  // pass `before` (the public_id of an earlier message) to scroll up.
  async subCommunityChat(publicId, { before, limit } = {}) {
    const params = new URLSearchParams();
    if (before) params.set("before", before);
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/chat${qs ? "?" + qs : ""}`,
    );
  },
  async subCommunityCreateChat(publicId, body) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/chat`, {
      method: "POST",
      body: { body },
    });
  },
  async subCommunityUpdateChat(publicId, messageId, body) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/chat/${encodeURIComponent(messageId)}`,
      { method: "PATCH", body: { body } },
    );
  },
  async subCommunityDeleteChat(publicId, messageId) {
    return apiFetch(
      `/api/sub-communities/${encodeURIComponent(publicId)}/chat/${encodeURIComponent(messageId)}`,
      { method: "DELETE" },
    );
  },

  // Sub-community disband + transfer ownership.
  async subCommunityDisband(publicId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}`, {
      method: "DELETE",
    });
  },
  async subCommunityTransfer(publicId, toUserId) {
    return apiFetch(`/api/sub-communities/${encodeURIComponent(publicId)}/transfer`, {
      method: "POST",
      body: { to_user_id: toUserId },
    });
  },

  logout() {
    clearSession();
  },
};

export { Api };